import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useCourse, useEnrollInCourse } from '@/hooks/use-course-api';
import { motion } from 'framer-motion';
import EmptyState from '@/components/common/EmptyState';
import { SEO, createBreadcrumbSchema } from '@/components/seo';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Star,
  Clock,
  Users,
  BookOpen,
  GraduationCap,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Play,
  Loader2,
  Tag,
  BarChart3,
  Target,
  UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const LEVEL_COLORS: Record<string, string> = {
  Beginner: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-400',
  Intermediate: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-400',
  Advanced: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-400',
  'All Levels': 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400',
};

const CourseDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { isRTL, t } = useLanguage();
  const { formatPrice } = useCurrency();
  const { isAuthenticated } = useAuth();
  const { data: course, isLoading } = useCourse(slug || '', true);
  const enrollMutation = useEnrollInCourse();

  const handleEnroll = async () => {
    if (!course) return;
    try {
      await enrollMutation.mutateAsync(course.name);
      toast.success(t('courseDetail.toast.enrollSuccess'));
    } catch {
      toast.error(t('courseDetail.toast.enrollFailed'));
    }
  };

  // Slug-derived fallbacks for prerender + crawlers; replaced with rich data
  // in the success path below.
  const slugTitle = (slug || '')
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  const fallbackCourseUrl = `https://hudanlilmuttaqeen.com/courses/${slug || ''}`;

  if (isLoading) {
    return (
      <Layout>
        <SEO
          title={`${slugTitle || t('nav.courses')} | Hudan Lil Muttaqeen`}
          description={t('courseDetail.seo.description')}
          url={fallbackCourseUrl}
          type="article"
        />
        <div className="container py-12" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="max-w-5xl mx-auto space-y-6">
            <div className="h-10 w-40 bg-muted/50 rounded-lg animate-pulse" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                <div className="h-48 bg-muted/50 rounded-xl animate-pulse" />
                <div className="h-32 bg-muted/50 rounded-xl animate-pulse" />
              </div>
              <div className="h-80 bg-muted/50 rounded-xl animate-pulse" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!course) {
    return (
      <Layout>
        <SEO
          title={`${t('courseDetail.notFound.title')} | Hudan Lil Muttaqeen`}
          description={t('courseDetail.notFound.description')}
          url={fallbackCourseUrl}
        />
        <div className="container py-20">
          <EmptyState
            icon={BookOpen}
            title={t('courseDetail.notFound.title')}
            description={t('courseDetail.notFound.description')}
            actionLabel={t('courseDetail.notFound.action')}
            actionHref="/courses"
          />
        </div>
      </Layout>
    );
  }

  const title = isRTL ? (course.title_ar || course.title) : course.title;
  const description = isRTL ? (course.description_ar || course.description) : course.description;
  const shortDesc = isRTL ? (course.short_description_ar || course.short_description) : course.short_description;
  const syllabus = isRTL ? (course.syllabus_ar || course.syllabus) : course.syllabus;
  const outcomes = isRTL ? (course.learning_outcomes_ar || course.learning_outcomes) : course.learning_outcomes;
  const prerequisites = isRTL ? (course.prerequisites_ar || course.prerequisites) : course.prerequisites;
  const targetAudience = isRTL ? (course.target_audience_ar || course.target_audience) : course.target_audience;
  const instructor = course.instructor_details;
  const levelColor = LEVEL_COLORS[course.level] || LEVEL_COLORS['All Levels'];
  const hasDiscount = course.discount_price && course.discount_price < course.price;

  const courseUrl = `https://hudanlilmuttaqeen.com/courses/${course.slug || course.name}`;
  const seoDescription = (shortDesc || description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 155);
  const effectivePrice = course.effective_price ?? course.price ?? 0;

  const courseStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@context": "https://schema.org",
        "@type": "Course",
        "name": title,
        "description": seoDescription,
        "provider": {
          "@type": "Organization",
          "name": "Hudan Lil Muttaqeen",
          "sameAs": "https://hudanlilmuttaqeen.com"
        },
        "offers": {
          "@type": "Offer",
          "price": course.is_free ? 0 : effectivePrice,
          "priceCurrency": course.currency || "USD",
          "availability": "https://schema.org/InStock",
          "url": courseUrl
        },
        "inLanguage": isRTL ? "ar" : "en",
        "url": courseUrl,
        ...(course.thumbnail ? { "image": course.thumbnail } : {}),
        ...(instructor ? {
          "instructor": {
            "@type": "Person",
            "name": instructor.full_name
          }
        } : {})
      },
      createBreadcrumbSchema(
        [
          { name: t('nav.home'), url: 'https://hudanlilmuttaqeen.com/' },
          { name: t('nav.courses'), url: 'https://hudanlilmuttaqeen.com/courses' },
          { name: title, url: courseUrl }
        ],
        isRTL ? 'ar' : 'en'
      )
    ]
  };

  return (
    <Layout>
      <SEO
        title={`${title} | Hudan Lil Muttaqeen`}
        description={seoDescription}
        image={course.thumbnail || undefined}
        url={courseUrl}
        type="article"
        structuredData={courseStructuredData}
      />
      <div className="container py-8 max-w-5xl" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Back */}
        <Link
          to="/courses"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          {isRTL ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          {t('courseDetail.backToCourses')}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Header */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex flex-wrap gap-2 mb-3">
                <Badge className={`text-xs ${levelColor}`}>{course.level}</Badge>
                {typeof course.category === 'string' ? (
                  <Badge variant="outline" className="text-xs">{course.category}</Badge>
                ) : course.category?.category ? (
                  <Badge variant="outline" className="text-xs">{course.category.category}</Badge>
                ) : null}
                {course.featured && (
                  <Badge className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400">
                    {t('courseDetail.badge.featured')}
                  </Badge>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-3">{title}</h1>

              {shortDesc && (
                <p className="text-muted-foreground leading-relaxed mb-4">{shortDesc}</p>
              )}

              {/* Stats */}
              <div className="flex items-center gap-4 flex-wrap text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                  <span className="font-medium text-foreground">{(course.average_rating || 0).toFixed(1)}</span>
                  <span>({course.total_reviews || 0})</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <Users className="h-4 w-4" />
                  {course.enrolled_students || 0} {t('courseDetail.stats.enrolled')}
                </span>
                <span className="flex items-center gap-1.5">
                  <BookOpen className="h-4 w-4" />
                  {course.total_lessons || 0} {t('teacherProfile.lessons')}
                </span>
                <span className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4" />
                  {course.total_duration_hours || 0} {t('courseDetail.stats.hours')}
                </span>
              </div>
            </motion.div>

            {/* Thumbnail / Video */}
            {(course.thumbnail || course.preview_video) && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
                <div className="aspect-video rounded-xl overflow-hidden bg-muted relative">
                  {course.thumbnail && (
                    <img src={course.thumbnail} alt={title} className="w-full h-full object-cover" />
                  )}
                  {course.preview_video && (
                    <a
                      href={course.preview_video}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={t('courseDetail.preview.ariaLabel')}
                      className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
                    >
                      <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center">
                        <Play className="h-7 w-7 text-primary ms-1" aria-hidden="true" />
                      </div>
                    </a>
                  )}
                </div>
              </motion.div>
            )}

            {/* Description */}
            {description && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <Card>
                  <CardContent className="pt-5">
                    <h2 className="text-lg font-semibold text-foreground mb-3">
                      {t('courseDetail.tabs.description')}
                    </h2>
                    <div className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line prose-sm">
                      {description}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Learning Outcomes */}
            {outcomes && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                <Card>
                  <CardContent className="pt-5">
                    <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Target className="h-5 w-5 text-primary" />
                      {t('courseDetail.tabs.whatYouLearn')}
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {outcomes.split('\n').filter(Boolean).map((item, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                          <span className="text-muted-foreground">{item.replace(/^[-•*]\s*/, '')}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Syllabus */}
            {syllabus && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <Card>
                  <CardContent className="pt-5">
                    <h2 className="text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-primary" />
                      {t('courseDetail.tabs.syllabus')}
                    </h2>
                    <Accordion type="multiple" className="space-y-1">
                      {syllabus.split('\n').filter(Boolean).map((item, i) => (
                        <AccordionItem key={i} value={`syl-${i}`} className="border-none">
                          <AccordionTrigger className="text-sm font-medium py-2 px-3 rounded-lg hover:bg-muted/50 hover:no-underline">
                            <span className="flex items-center gap-2">
                              <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs flex items-center justify-center font-semibold">
                                {i + 1}
                              </span>
                              {item.replace(/^[-•*\d.)\s]+/, '')}
                            </span>
                          </AccordionTrigger>
                          <AccordionContent className="text-sm text-muted-foreground ps-11">
                            {t('courseDetail.lesson.lockedNote')}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Prerequisites & Target Audience */}
            {(prerequisites || targetAudience) && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {prerequisites && (
                    <Card>
                      <CardContent className="pt-5">
                        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                          <GraduationCap className="h-4 w-4 text-primary" />
                          {t('courseDetail.tabs.prerequisites')}
                        </h3>
                        <div className="space-y-1.5">
                          {prerequisites.split('\n').filter(Boolean).map((item, i) => (
                            <p key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                              <span className="text-primary mt-1">&#8226;</span>
                              {item.replace(/^[-•*]\s*/, '')}
                            </p>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {targetAudience && (
                    <Card>
                      <CardContent className="pt-5">
                        <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                          <UserCheck className="h-4 w-4 text-primary" />
                          {t('courseDetail.tabs.whoThisIsFor')}
                        </h3>
                        <div className="space-y-1.5">
                          {targetAudience.split('\n').filter(Boolean).map((item, i) => (
                            <p key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                              <span className="text-primary mt-1">&#8226;</span>
                              {item.replace(/^[-•*]\s*/, '')}
                            </p>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </motion.div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Price & Enroll CTA */}
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
              <Card className="border-primary/20 bg-primary/[0.02] sticky top-24">
                <CardContent className="pt-5">
                  {/* Price */}
                  <div className="text-center mb-4">
                    {course.is_free ? (
                      <div className="text-3xl font-bold text-emerald-600">{t('teacherProfile.free')}</div>
                    ) : (
                      <div>
                        <div className="text-3xl font-bold text-foreground">
                          {formatPrice(course.effective_price || course.price)}
                        </div>
                        {hasDiscount && (
                          <div className="flex items-center justify-center gap-2 mt-1">
                            <span className="text-sm text-muted-foreground line-through">{formatPrice(course.price)}</span>
                            <Badge className="text-xs bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400">
                              {Math.round((1 - (course.discount_price! / course.price)) * 100)}% {t('courseDetail.pricing.off')}
                            </Badge>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {isAuthenticated ? (
                    <Button
                      className="w-full"
                      onClick={handleEnroll}
                      disabled={enrollMutation.isPending}
                    >
                      {enrollMutation.isPending && <Loader2 className="h-4 w-4 animate-spin me-2" />}
                      {t('courses.enrollNow')}
                    </Button>
                  ) : (
                    <Button asChild className="w-full">
                      <Link to="/register/student">
                        {t('courseDetail.pricing.signUpToEnroll')}
                      </Link>
                    </Button>
                  )}

                  {/* Course info list */}
                  <div className="mt-5 space-y-3 pt-5 border-t border-border">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <BookOpen className="h-4 w-4" />
                        {t('courseDetail.card.lessons')}
                      </span>
                      <span className="font-medium text-foreground">{course.total_lessons || 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Clock className="h-4 w-4" />
                        {t('courses.duration')}
                      </span>
                      <span className="font-medium text-foreground">{course.total_duration_hours || 0}h</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <BarChart3 className="h-4 w-4" />
                        {t('courses.level')}
                      </span>
                      <span className="font-medium text-foreground">{course.level}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        {t('courseDetail.card.enrolled')}
                      </span>
                      <span className="font-medium text-foreground">{course.enrolled_students || 0}</span>
                    </div>
                    {course.total_completions > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4" />
                          {t('courseDetail.card.completed')}
                        </span>
                        <span className="font-medium text-foreground">{course.total_completions}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Instructor Card */}
            {instructor && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                <Card>
                  <CardContent className="pt-5">
                    <h3 className="text-sm font-semibold text-foreground mb-3">
                      {t('courses.instructor')}
                    </h3>
                    <Link
                      to={`/teachers/${instructor.name}`}
                      className="flex items-center gap-3 group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-primary/10 overflow-hidden shrink-0">
                        {instructor.profile_image ? (
                          <img src={instructor.profile_image} alt={instructor.full_name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-lg font-bold text-primary">{instructor.full_name?.charAt(0)}</span>
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                          {isRTL ? (instructor.full_name_ar || instructor.full_name) : instructor.full_name}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {instructor.average_rating && (
                            <span className="flex items-center gap-0.5">
                              <Star className="h-3 w-3 text-amber-500 fill-amber-500" />
                              {instructor.average_rating.toFixed(1)}
                            </span>
                          )}
                          {instructor.total_sessions && (
                            <span>{instructor.total_sessions} sessions</span>
                          )}
                        </div>
                      </div>
                    </Link>
                    {instructor.bio && (
                      <p className="text-xs text-muted-foreground mt-3 line-clamp-3">
                        {isRTL ? (instructor.bio_ar || instructor.bio) : instructor.bio}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Tags */}
            {course.category && (
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <Card>
                  <CardContent className="pt-5">
                    <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Tag className="h-4 w-4 text-primary" />
                      {t('courses.category')}
                    </h3>
                    <Badge variant="secondary">
                      {typeof course.category === 'string' ? course.category : course.category?.category}
                    </Badge>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CourseDetailPage;
